import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from '../../core/auth.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CustomersFacadeService } from '../../services/customers/customers-facade.service';
import { AlertsFacadeService } from '../../services/alerts/alerts-facade.service';
import { VersionFacadeService } from '../../services/version/version-facade.service';
import { HelperUtilities } from '../../shared/utilities/helper-utilities';
import { environment } from '../../../environments/environment';
import { Role } from '../../../enums/enums';
import { ROLE_LABELS } from '../../../constants/constants';
import { SHARED_MATERIAL_IMPORTS, SHARED_COMPONENT_IMPORTS } from '../../shared/shared-imports';
import { MatSnackBar } from '@angular/material/snack-bar';

const COMPACT_LAYOUT = '(max-width: 1024px)';

@Component({
  selector: 'app-nav-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatToolbarModule,
    MatSidenavModule,
    ...SHARED_MATERIAL_IMPORTS,
    ...SHARED_COMPONENT_IMPORTS,
  ],
})
export class NavigationBarComponent implements OnInit {
  public authservice = inject(AuthService);
  public user = this.authservice.user;
  public lastUpdated: string = '';
  public version = environment.version;
  public customersFacadeService = inject(CustomersFacadeService);
  private alertsFacade = inject(AlertsFacadeService);
  public unresolvedVenueCount$ = this.alertsFacade.unresolvedVenueCount$;
  private versionFacade = inject(VersionFacadeService);
  public backendVersion$ = this.versionFacade.backendVersion$;
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private breakpoints = inject(BreakpointObserver);
  public isAdmin = false;
  public isViewOnly = false;

  public isCompact = toSignal(
    this.breakpoints.observe(COMPACT_LAYOUT).pipe(map((state) => state.matches)),
    { initialValue: this.breakpoints.isMatched(COMPACT_LAYOUT) },
  );
  public drawerOpen = signal(false);

  public roleLabel = computed(() => {
    const role = this.authservice.roles()?.[0];
    return role == null ? '' : (ROLE_LABELS[role as Role] ?? '');
  });

  public ngOnInit(): void {
    this.lastUpdated = HelperUtilities.getCurrentTime();
    this.isAdmin = this.authservice.isAdmin();
    this.isViewOnly = this.authservice.hasRole(Role.ViewOnly);

    if (this.isAdmin || this.isViewOnly) {
      this.alertsFacade.loadAlerts();
    }
  }

  public toggleDrawer(): void {
    this.drawerOpen.update((open) => !open);
  }

  public closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  public onSearch(value: string): void {
    this.customersFacadeService.setFilter(value);
  }

  public openDetails(customerSelected: { systemId?: string | number }): void {
    const systemId: string = (customerSelected.systemId ?? '').toString().toUpperCase();
    if (systemId) {
      this.customersFacadeService.selectedCustomer(systemId);
      this.router.navigate(['/customers', systemId]);
    } else {
      this.snackBar.open('No data available for this customer', 'Close', {
        duration: 1000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    }
  }

  public refreshData(): void {
    this.authservice.triggerRefresh();
    this.lastUpdated = HelperUtilities.getCurrentTime();

    if (this.isAdmin || this.isViewOnly) {
      this.alertsFacade.loadAlerts();
    }
  }

  async logout(): Promise<void> {
    await this.authservice.waitForIdleInteraction();
    this.authservice.logoutAccount();
  }
}
